import { useState } from 'react'
import { Switch } from '@headlessui/react'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import KinesisProgramWeekKeyIcon from 'components/Athlete/v2/KinesisProgramWeeks/KinesisProgramWeekKeyIcon'

export default function PeriodizationVisualizer({
    label = '',
    periodizationArray = []
}) {
    const [blockSpacing, setBlockSpacing] = useState(false)
    const [isCollapsed, setIsCollapsed] = useState(false)

    const GetCleanWeekTooltipJSON = (week) => {

        // Removes the sets and reps data

        if (!week || !week.volumeData) return JSON.stringify(week, null, 4)

        const {
            setsAndRepsData,
            plyometricSetsAndRepsOverrideData,
            ...volumeDataWithoutSetsAndReps
        } = week.volumeData

        const weekWithoutSetsAndReps = {
            ...week,
            volumeData: volumeDataWithoutSetsAndReps
        }

        return JSON.stringify(weekWithoutSetsAndReps, null, 4)
    }

    const GetWeekBarStyle = (volumeData) => {

        const programPhaseKey = volumeData.programPhaseKey
        const taperLevel = volumeData.taperData.taperLevel
        const volumeProgressionModifierApplied = volumeData?.volumeProgressionModifierApplied ?? false

        var style = 'flex items-end rounded cursor-pointer hover:opacity-80 '

        if (volumeProgressionModifierApplied === true){ 
            style += 'bg-gray-500 '
        }

        if (volumeProgressionModifierApplied === false) {
            if (programPhaseKey === 're-introduction') style += 'bg-orange-100 '
            if (programPhaseKey === 'reload') style += 'bg-orange-100 '
            if (programPhaseKey === 'off-season') style += 'bg-orange-200 '
            if (programPhaseKey === 'early-season') style += 'bg-orange-300 '
            if (programPhaseKey === 'in-season') style += 'bg-orange-400 '
            if (programPhaseKey === 'competition') style += 'bg-orange-500 '
            if (programPhaseKey === 'championship') style += 'bg-orange-600 '
            if (programPhaseKey === 'post-race-deload') style += 'bg-orange-100 '
        }

        if (taperLevel === 'taper-none') style += 'h-16 '
        if (taperLevel === 'taper-one') style += 'h-12 '
        if (taperLevel === 'taper-two') style += 'h-10 '
        if (taperLevel === 'taper-three') style += 'h-6 '

        return style
    }

    return (
        <div className="relative py-4 px-4 bg-gradient-to-b from-zinc-800 to-zinc-600 rounded-xl">
            <div 
                className="relative flex items-center  cursor-pointer"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div 
                    className="flex items-center pl-3 py-1 border border-gray-300 rounded-full cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation()
                        setBlockSpacing(!blockSpacing)
                    }}
                >  
                    <p className='text-xs w-[100px] text-white leading-none'>Block Spacing</p>

                    <div className="flex items-center pr-2">
                        <Switch
                            checked={blockSpacing}
                            className={`
                                relative inline-flex h-4 w-8 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                                ${blockSpacing ? 'bg-orange-600' : 'bg-gray-200'}
                            `}
                        >
                            <span className="sr-only">Use setting</span>
                            <span
                                aria-hidden="true"
                                className={`
                                    pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                    ${blockSpacing ? 'translate-x-4' : 'translate-x-0'}
                                `}
                            />
                        </Switch>
                    </div>
                </div>

                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="text-center text-white whitespace-nowrap">
                        {label}
                    </div>
                </div>

                <div className="ml-auto flex items-center cursor-pointer text-white hover:text-gray-300">
                    {isCollapsed ? (
                        <ChevronDownIcon className="w-5 h-5" />
                    ) : (
                        <ChevronUpIcon className="w-5 h-5" />
                    )}
                </div>
            </div>

            {!isCollapsed && (
                <div className="mt-4 mx-auto flex items-end justify-center gap-x-2">
                    <div className="flex items-end gap-x-0.5">
                        {periodizationArray.map((week, weekIndex) => {
                            const currentBlockIndex = week.volumeData?.blockIndex ?? 0
                            const previousBlockIndex = weekIndex > 0 
                                ? (periodizationArray[weekIndex - 1].volumeData?.blockIndex ?? 0)
                                : currentBlockIndex
                            const isBlockStart = weekIndex === 0 || currentBlockIndex !== previousBlockIndex
                            
                            return (
                                <div
                                    key={weekIndex}
                                    className={`
                                        group relative w-6 h-24 flex flex-col items-center justify-end
                                        ${blockSpacing && isBlockStart && weekIndex > 0 ? 'ml-2' : ''}
                                    `}
                                >
                                    <div
                                        className={`${GetWeekBarStyle(week.volumeData)}`}
                                    >
                                        <p className="w-6 text-center text-sm pb-0.5">
                                            {week.programWeekIndex + 1} 
                                            {/* <span className="w-10">({week.volumeData.blockIndex})</span> */}
                                        </p>
                                    </div>
                                    
                                    <div className='pt-2'>
                                        <KinesisProgramWeekKeyIcon displaySize="small" programWeekKey={week.volumeData.programWeekKey} />
                                    </div>

                                    <div className="pointer-events-none absolute left-1/2 -top-2 z-20 hidden w-[320px] -translate-x-1/2 -translate-y-full rounded-md bg-black p-2 text-[10px] font-mono text-white whitespace-pre group-hover:block">
                                        {GetCleanWeekTooltipJSON(week)}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}